import { Box, Stack, Typography, Link } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { GET_ALL_PHARMACY_PRODUCTS } from "utils/reactQueryKeys";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

function ImportProductsModal({ closeModal }) {
  const [file, setFile] = useState(null);
  const queryClient = useQueryClient();

  const { mutate: importProducts, isLoading } = useCustomMutation(
    {
      url: `/product/import`,
      method: "post",
      // Content-Type header is usually handled automatically by browser when using FormData
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_ALL_PHARMACY_PRODUCTS]);
        toast.success("Products imported successfully");
        closeModal();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to import products");
      },
    }
  );

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!file) return toast.warning("Please select a file first");
    
    const formData = new FormData();
    formData.append("file", file); // Key must match Backend Controller @UseInterceptors
    importProducts(formData);
  };

  const handleDownloadTemplate = () => {
    // Create a temporary link element to trigger the download
    const link = document.createElement("a");
    // This path points to the 'public/templates' folder we created in Step 1
    link.href = "/csv-sample.csv"; 
    link.setAttribute("download", "csv-sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack spacing={3} p={2} alignItems="center">
      <Typography variant="body1" textAlign="center">
        Upload a CSV file to bulk import products. Please ensure you strictly use the below template 
        <br />
        Ensure columns match: <i>drugName, brandName, strength, etc.</i>
      </Typography>

      <CustomButton 
        text="Download CSV Template"
        variant="outlined" 
        color="info" // or "primary" depending on your theme
        startIcon={<DownloadOutlinedIcon />}
        onClick={handleDownloadTemplate}
        sx={{ width: "fit-content" }}
      />

      <Box
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          p: 4,
          width: "100%",
          textAlign: "center",
          cursor: "pointer",
          "&:hover": { borderColor: "primary.main" },
        }}
        component="label"
      >
        <input
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileChange}
        />
        <FileUploadOutlinedIcon sx={{ fontSize: 40, color: "gray", mb: 1 }} />
        <Typography>
          {file ? file.name : "Click to Select CSV File"}
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} width="100%" justifyContent="center">
        <CustomButton
          text="Upload"
          variant="contained"
          color="secondary"
          onClick={handleUpload}
          disabled={isLoading || !file}
        />
        <CustomButton
          text="Cancel"
          variant="outlined"
          onClick={closeModal}
        />
      </Stack>
    </Stack>
  );
}

export default ImportProductsModal;